import React, { useState } from 'react';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import {
  useGetLfaConversionDataQuery,
  useUpdateLfaConversionMutation,
  type Lfa_Service_Feedback,
} from '~/graphql/types';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import Loading from '~/components/atom/Loading';
import ErrorScreen from '~/components/page/ErrorScreen';
import Icon from '~/components/atom/Icon';
import { Body, Heading3 } from '~/components/atom/Typography';
import Divider from '~/components/atom/Divider';
import servicesFeedbackState from '../../state/servicesFeedbackState';
import AppointmentHappened from './components/AppointmentHappened';
import AppointmentNotHappened from './components/AppointmentNotHappened';
import Footer from './components/Footer';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import Catalog from '~/Catalog';
import useFeedbackValidation from '../../hooks/useFeedbackValidation';
import validateFeedback from '../../hooks/useFeedbackValidation/utils/validateFeedback';
import hasValidationErrors from '../../utils/hasValidationErrors';
import DidAppointmentHappen from './components/DidAppointmentHappen';
import type { ClientLfaConversion } from '../../types';
import useTrackEvent from '~/hooks/useTrackEvent';

const text = {
  title: 'Status updaten',
  description:
    'Na afloop van een ingeplande afspraak horen wij graag wat de uitkomst is. Door de status te updaten kunnen wij onze dienstverlening leveren en verbeteren. Het updaten van de status is verplicht.',

  successMessage: 'Status opgeslagen',
};

export type Props = {
  dataTestId?: string;
  className?: string;
  conversionId: string;
  conversionVersion: number;
  selectedServices: ClientLfaConversion['selectedServices'];
  lastServiceFeedback?: Array<Lfa_Service_Feedback> | null;
  feedbackFor: string;
  onClose: () => void;
};

const FeedbackModal: React.FCC<Props> = ({
  selectedServices,
  conversionId,
  conversionVersion,
  lastServiceFeedback,
  onClose,
  feedbackFor,
}) => {
  const { id: accountId } = useCurrentAccount();
  const trackEvent = useTrackEvent();
  const addToast = useAddToast();
  // Main question in the decision tree
  const [hasAppointmentHappened, setHasAppointmentHappened] = useState(true);
  const { setValidationErrors, clearValidationErrors } = useFeedbackValidation(
    {},
  );
  const servicesFeedback = useRecoilValue(servicesFeedbackState);

  const [updateFeedback, { loading: updateLoading }] =
    useUpdateLfaConversionMutation();
  const { data, error, loading } = useGetLfaConversionDataQuery({
    variables: { accountId },
  });

  if (loading) return <Loading />;
  if (error || !data || !data.getLfaConversionData) return <ErrorScreen />;

  const { getLfaConversionData } = data;

  const handleUpdateFeedback = async () => {
    if (servicesFeedback) {
      const validatedFeedback = validateFeedback(servicesFeedback);

      if (hasValidationErrors(validatedFeedback)) {
        return setValidationErrors(validatedFeedback);
      } else {
        await updateFeedback({
          variables: {
            accountId,
            lfaConversionId: conversionId,
            lfaConversionVersion: conversionVersion,
            update: servicesFeedback,
          },
        }).then(({ data, errors }) => {
          if (!data || (errors && errors.length > 0)) {
            return addToast([
              formatToastMessage(Catalog.genericUnknownErrorMessage, 'danger'),
            ]);
          }

          trackEvent({
            eventName: 'lfa_conversion_feedback',
            parameters: {
              lfa_conversion_id: conversionId,
              status:
                data.updateLfaConversion.lastServiceFeedbackStatus?.toString(),
            },
          });

          addToast([formatToastMessage(text.successMessage, 'success')]);
          clearValidationErrors();
          onClose();
        });
      }
    }
  };

  const toggleAppointmentHappened = (bool: boolean) => {
    setHasAppointmentHappened(bool);
    clearValidationErrors();
  };

  return (
    <Overlay onClose={onClose}>
      <Modal onClose={onClose} maxWidth="800px">
        <JustificationContainer padding={['xl']} direction="column">
          <JustificationContainer direction="column" gap="base">
            <JustificationContainer align="center" gap="base">
              <StyledIcon
                size="l"
                name="feedback"
                background={{ group: 'primary' }}
                color={{ group: 'white' }}
              />
              <Heading3
                margin={[null]}
              >{`${text.title}: ${feedbackFor}`}</Heading3>
            </JustificationContainer>
            <Body size="base" margin={[null]}>
              {text.description}
            </Body>
          </JustificationContainer>

          <Divider margin={['m', null]} />

          <DidAppointmentHappen
            hasAppointmentHappened={hasAppointmentHappened}
            toggleAppointmentHappened={toggleAppointmentHappened}
          />

          {hasAppointmentHappened && (
            <AppointmentHappened
              selectedServices={selectedServices}
              lastServiceFeedback={lastServiceFeedback}
              enabledServices={getLfaConversionData.enabledServices}
            />
          )}
          {!hasAppointmentHappened && (
            <AppointmentNotHappened
              conversionId={conversionId}
              rejectionReasons={getLfaConversionData.rejectionReasons}
            />
          )}
        </JustificationContainer>

        <Footer
          loading={updateLoading}
          onClose={() => {
            clearValidationErrors();
            onClose();
          }}
          onClick={handleUpdateFeedback}
          servicesFeedback={servicesFeedback}
        />
      </Modal>
    </Overlay>
  );
};

const StyledIcon = styled(Icon)`
  & > svg {
    height: 0.8em;
    width: 0.8em;
  }
`;

export default FeedbackModal;
